import { useLazyQuery } from '@apollo/client';
import { Query, QueryGetTenantNameArgs } from 'graphql/generated/graphqlTypes';
import { GET_TENANT_NAME } from 'graphql/queries';
import { useEffect, useRef, useState } from 'react';
import { BaseHookProps } from 'v2/hooks/hooks.types';

interface Props extends BaseHookProps {
  azureTenantId: string | null;
}

const NUMBER_OF_RETRY_BEFORE_ERROR = 60;
const TIME_TO_NEXT_RETRY = 3000;

const useTenantName = ({ handleFetchError, azureTenantId }: Props) => {
  const [retryCount, setRetryCount] = useState(0);
  const [getTenantNameQuery, { loading, data }] = useLazyQuery<Pick<Query, 'getTenantName'>, QueryGetTenantNameArgs>(
    GET_TENANT_NAME
  );

  // Keep the latest error handler reachable without making it an effect dependency,
  // so a new handler identity from the parent doesn't restart the poll.
  const handleFetchErrorRef = useRef(handleFetchError);
  handleFetchErrorRef.current = handleFetchError;

  useEffect(() => {
    if (!azureTenantId) return undefined;

    let attempt = 0;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const scheduleNextAttempt = () => {
      if (attempt >= NUMBER_OF_RETRY_BEFORE_ERROR) {
        handleFetchErrorRef.current('Error while fetching tenant name');
        return;
      }
      timeoutId = setTimeout(runQuery, TIME_TO_NEXT_RETRY);
    };

    const runQuery = () => {
      attempt += 1;
      setRetryCount((prev) => prev + 1);
      getTenantNameQuery({ variables: { azureTenantId } })
        .then((result) => {
          if (cancelled || result.data?.getTenantName !== undefined) return;
          scheduleNextAttempt();
        })
        .catch(() => {
          if (!cancelled) scheduleNextAttempt();
        });
    };

    runQuery();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [azureTenantId, getTenantNameQuery]);

  const isLoading = retryCount !== 0 && data?.getTenantName === undefined && loading;
  return {
    tenantName: data?.getTenantName,
    loading: isLoading
  };
};

export default useTenantName;
